import posthog from 'posthog-js';

/**
 * Initialise PostHog. Safe to call multiple times — subsequent calls are no-ops.
 * Reads VITE_POSTHOG_KEY and VITE_POSTHOG_HOST from the environment.
 * If VITE_POSTHOG_KEY is not set, all calls below are no-ops.
 */
export function initAnalytics(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) return;

  const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';

  posthog.init(key, {
    api_host: host,
    person_profiles: 'identified_only', // Only create profiles for logged-in users
    capture_pageview: false,             // We fire pageviews manually via usePageTracking
    capture_pageleave: true,
    autocapture: false,                  // Disable on mobile — too noisy, uses bandwidth
    persistence: 'localStorage',
  });
  posthog.register({ app_version: import.meta.env.VITE_APP_VERSION ?? 'unknown' });
}

/** Attach a Supabase user to subsequent events. Call after sign-in + profile load. */
export function identifyUser(userId: string, properties: {
  role: 'admin' | 'scorer' | 'viewer';
  displayName: string;
}): void {
  if (!posthog.__loaded) return;
  posthog.identify(userId, {
    role: properties.role,
    display_name: properties.displayName,
  });
}

/** Detach the user (reset distinct_id to anonymous). Call on sign-out. */
export function resetUser(): void {
  if (!posthog.__loaded) return;
  posthog.reset();
}

/** Fire a manual $pageview event for the current path. */
export function capturePageView(path: string): void {
  if (!posthog.__loaded) return;
  posthog.capture('$pageview', {
    $current_url: window.location.origin + path,
    path,
  });
}

/** General-purpose event capture. All app-specific events go through here. */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!posthog.__loaded) return;
  posthog.capture(event, properties);
}
